import React, { useEffect, useRef, useState } from 'react';
import CategoryIcon from './CategoryIcon.jsx';

// The sections dropdown (web parity W4, Tom 2026-07-30: "a category sections
// drop down menu") — web's version of the iOS Today title dropdown. One
// control, two jobs: it names where you are ("Daily Six" or a category) and
// it is the door to everywhere else. Renders ONLY when the §3f sections gate
// is open and at least one category has a section today — the public sees
// nothing until Tom flips the flag.
export default function SectionsDropdown({ active, categories, onSelect }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Outside click / Escape closes. Plain listeners over a portal: the menu is
  // small, anchored, and lives inside the page flow.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (value) => {
    setOpen(false);
    onSelect(value);
  };

  return (
    <div ref={rootRef} style={wrap}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={trigger}
      >
        {active ? (
          <span style={triggerLabel}>
            <CategoryIcon category={active} size={18} />
            {active}
          </span>
        ) : (
          <span style={triggerLabel}>
            <span style={sixBadge} aria-hidden="true">6</span>
            Daily Six
          </span>
        )}
        <span aria-hidden="true" style={{ fontSize: 11, color: 'var(--gold)' }}>▼</span>
      </button>

      {open && (
        <div role="listbox" aria-label="Choose a section" style={menu}>
          <button
            role="option"
            aria-selected={!active}
            onClick={() => pick(null)}
            style={{ ...item, ...(active ? null : itemActive) }}
          >
            <span style={sixBadge} aria-hidden="true">6</span>
            Daily Six
          </button>
          {categories.map((c) => (
            <button
              key={c}
              role="option"
              aria-selected={active === c}
              onClick={() => pick(c)}
              style={{ ...item, ...(active === c ? itemActive : null) }}
            >
              <CategoryIcon category={c} size={18} />
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const wrap = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  margin: '0 0 14px',
  zIndex: 20,
};
const trigger = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid var(--border)',
  background: 'var(--card-bg)',
  color: 'var(--primary-text)',
  fontSize: 16,
  fontWeight: 800,
  cursor: 'pointer',
};
const triggerLabel = { display: 'inline-flex', alignItems: 'center', gap: 7 };
const sixBadge = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: 10,
  border: '1.5px solid var(--gold)',
  color: 'var(--gold)',
  fontSize: 12,
  fontWeight: 900,
  lineHeight: 1,
};
const menu = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: '50%',
  transform: 'translateX(-50%)',
  minWidth: 210,
  background: 'var(--card-bg)',
  border: '1px solid var(--border)',
  borderRadius: 14,
  padding: 6,
  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};
const item = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '10px 12px',
  borderRadius: 10,
  border: 'none',
  background: 'transparent',
  color: 'var(--primary-text)',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  textAlign: 'left',
};
const itemActive = {
  background: 'color-mix(in srgb, var(--gold) 14%, transparent)',
  color: 'var(--gold)',
};
